 function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import styled from '@emotion/styled'
import {ExpandMore} from '@mui/icons-material'
import graphql from 'babel-plugin-relay/macro'
import React, { useEffect, useRef, useState} from 'react'
import {useFragment} from 'react-relay'
import useAtmosphere from '~/hooks/useAtmosphere'
import {MenuPosition} from '~/hooks/useCoords'
import useMenu from '~/hooks/useMenu'
import useMutationProps from '~/hooks/useMutationProps'
import {PALETTE} from '~/styles/paletteV3'
import getNonNullEdges from '~/utils/getNonNullEdges'

import useForm from '../hooks/useForm'
import {PortalStatus} from '../hooks/usePortal'
import useTimedState from '../hooks/useTimedState'
import CreateTaskMutation from '../mutations/CreateTaskMutation'
import UpdatePokerScopeMutation from '../mutations/UpdatePokerScopeMutation'

import convertToTaskContent from '../utils/draftjs/convertToTaskContent'
import Legitity from '../validation/Legitity'

import Checkbox from './Checkbox'
import NewGitLabIssueMenu from './NewGitLabIssueMenu'
import PlainButton from './PlainButton/PlainButton'
import StyledError from './StyledError'

const StyledButton = styled(PlainButton)({
  alignItems: 'center',
  backgroundColor: 'transparent',
  display: 'flex',
  height: '20px',
  justifyContent: 'flex-start',
  margin: 0,
  opacity: 1,
  width: 'fit-content',
  ':hover, :focus': {
    backgroundColor: 'transparent'
  }
})

const StyledIcon = styled(ExpandMore)({
  color: PALETTE.SKY_500,
  height: 20,
  width: 20,
  padding: 0,
  alignContent: 'center'
})

const StyledLink = styled('a')({
  color: PALETTE.SKY_500,
  display: 'block',
  fontSize: 12,
  lineHeight: '20px',
  textDecoration: 'none',
  '&:hover,:focus': {
    textDecoration: 'underline'
  }
})

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
})

const Item = styled('div')({
  backgroundColor: PALETTE.SLATE_100,
  cursor: 'pointer',
  display: 'flex',
  paddingLeft: 16,
  paddingTop: 8,
  paddingBottom: 8
})

const Issue = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 16,
  width: '100%'
})

const NewIssueInput = styled('input')({
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  color: PALETTE.SLATE_700,
  fontSize: 16,
  margin: 0,
  padding: '0px 8px 0px 0px',
  outline: 0,
  width: '100%'
})

const Error = styled(StyledError)({
  fontSize: 13,
  textAlign: 'left',
  width: '100%'
})








const validateIssue = (issue) => {
  return new Legitity(issue).trim().min(2, `C’mon, you call that an issue?`)
}

const NewGitLabIssueInput = (props) => {
  const {isEditing, meetingId, setIsEditing, viewerRef} = props
  const viewer = useFragment(
    graphql`
      fragment NewGitLabIssueInput_viewer on User {
        id
        team(teamId: $teamId) {
          id
        }
        teamMember(teamId: $teamId) {
          integrations {
            gitlab {
              api {
                errors {
                  message
                  locations {
                    line
                    column
                  }
                  path
                }
                # use alias to tell relay that this query shouldn't be cached with GitLabScopingSearchResults query
                newIssueQuery: query {
                  projects(membership: true, first: 100, sort: "latest_activity_desc") {
                    edges {
                      node {
                        ... on _xGitLabProject {
                          __typename
                          id
                          fullPath
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    viewerRef
  )
  const {id: userId, team, teamMember} = viewer
  const {id: teamId} = team
  const nullableEdges = _nullishCoalesce(_optionalChain([teamMember, 'optionalAccess', _ => _.integrations, 'optionalAccess', _2 => _2.gitlab, 'optionalAccess', _3 => _3.api, 'optionalAccess', _4 => _4.newIssueQuery, 'optionalAccess', _5 => _5.projects, 'optionalAccess', _6 => _6.edges]), () => ( []))
  const gitlabProjects = getNonNullEdges(nullableEdges).map(({node}) => node)
  const atmosphere = useAtmosphere()
  const {onCompleted, onError} = useMutationProps()
  const [createTaskError, setCreateTaskError] = useTimedState()
  useEffect(() => {
    if (isEditing) {
      setCreateTaskError(undefined)
    }
  }, [isEditing])
  const [selectedFullPath, setSelectedFullPath] = useState(_optionalChain([gitlabProjects, 'access', _7 => _7[0], 'optionalAccess', _8 => _8.fullPath]) || '')
  const {fields, onChange, validateField, setDirtyField} = useForm({
    newIssue: {
      getDefault: () => '',
      validate: validateIssue
    }
  })
  const {originRef, menuPortal, menuProps, togglePortal, portalStatus} = useMenu(
    MenuPosition.UPPER_LEFT,
    {isDropdown: true}
  )
  const ref = useRef(null)
  const {dirty, error} = fields.newIssue
  useEffect(() => {
    if (portalStatus === PortalStatus.Exited) {
      _optionalChain([ref, 'access', _9 => _9.current, 'optionalAccess', _10 => _10.focus, 'call', _11 => _11()])
    }
  }, [portalStatus])

  const handleCreateNewIssue = (e) => {
    e.preventDefault()
    if (portalStatus !== PortalStatus.Exited || !selectedFullPath) return
    const {newIssue: newIssueRes} = validateField()
    const {value: newIssueTitle, error} = newIssueRes
    if (error) {
      setDirtyField()
      return
    }
    setIsEditing(false)
    fields.newIssue.resetValue()
    if (!newIssueTitle.length) {
      fields.newIssue.dirty = false
      return
    }
    const newTask = {
      teamId,
      userId,
      meetingId,
      content: convertToTaskContent(`${newIssueTitle} #archived`),
      plaintextContent: newIssueTitle,
      status: 'active' ,
      integration: {
        service: 'gitlab' ,
        serviceProjectHash: selectedFullPath
      }
    }
    const handleCompleted = (res) => {
      const {error, task} = res.createTask
      if (error) {
        setCreateTaskError(error.message)
      }
      if (error || !task) return
      const {integrationHash} = task
      if (!integrationHash) return
      const pokerScopeVariables = {
        meetingId,
        updates: [
          {
            service: 'gitlab',
            serviceTaskId: integrationHash,
            action: 'ADD'
          } 
        ]
      }
      UpdatePokerScopeMutation(atmosphere, pokerScopeVariables, {
        onError,
        onCompleted,
        contents: [newIssueTitle]
      })
    }
    CreateTaskMutation(atmosphere, {newTask}, {onError, onCompleted: handleCompleted})
  }

  if (createTaskError) {
    return (
      React.createElement(Item, null
        , React.createElement(Checkbox, { active: true, disabled: true,} )
        , React.createElement(Issue, null
          , React.createElement(Error, null, createTaskError)
          , React.createElement(StyledLink, null, selectedFullPath)
        )
      )
    )
  }
  if (!isEditing) return null
  return (
    React.createElement(React.Fragment, null
      , React.createElement(Item, null
        , React.createElement(Checkbox, { active: true,} )
        , React.createElement(Issue, null
          , React.createElement(Form, { onSubmit: handleCreateNewIssue,}
            , React.createElement(NewIssueInput, {
              autoFocus: true,
              onBlur: handleCreateNewIssue,
              onChange: onChange,
              maxLength: 255,
              name: "newIssue",
              placeholder: "New issue title"  ,
              ref: ref,
              type: "text",}
            )
            , dirty && error && React.createElement(Error, null, error)
          )
          , React.createElement(StyledButton, { ref: originRef, onMouseDown: togglePortal,}
            , React.createElement(StyledLink, null, selectedFullPath)
            , React.createElement(StyledIcon, null )
          )
        )
      )
      , menuPortal(
        React.createElement(NewGitLabIssueMenu, {
          gitlabProjects: gitlabProjects,
          handleSelectFullPath: setSelectedFullPath,
          menuProps: menuProps,}
        )
      )
    )
  )
}

export default NewGitLabIssueInput
